import { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/button";
import { api } from "@/modules/api";

const Section = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
`;

const PlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AudioElement = styled.audio`
  width: 100%;
  height: 40px;
  border-radius: 8px;

  &::-webkit-media-controls-panel {
    background: #f8fafc;
  }
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetadataLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetadataValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
`;

const FileStatus = styled.span<{ available: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ available }) => (available ? "#16a34a" : "#9ca3af")};

  &::before {
    content: "${({ available }) => (available ? "✓" : "○")}";
    font-weight: 600;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const NoAudioMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
`;

interface AudioPlayerSectionProps {
  capturedAudioId: string;
  hasFile: boolean;
  hasMp3: boolean;
  durationSeconds: number | null;
  mimeType: string;
  filename: string;
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const AudioPlayerSection = ({
  capturedAudioId,
  hasFile,
  hasMp3,
  durationSeconds,
  mimeType,
  filename: _filename,
}: AudioPlayerSectionProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const signedUrlsQuery = useQuery({
    queryKey: ["captured-audio-signed-urls", capturedAudioId],
    queryFn: async () => {
      const response = await api.get(
        "/internal/v1/captured-audio/{captured_audio_id}/signed-urls",
        {
          params: { path: { captured_audio_id: capturedAudioId } },
        }
      );
      return response.data;
    },
    enabled: Boolean(capturedAudioId) && (hasFile || hasMp3),
    staleTime: 5 * 60 * 1000, // URLs are valid for a while
  });

  // Set audio URL when query completes - prefer MP3 for browser compatibility
  useEffect(() => {
    if (signedUrlsQuery.data) {
      const url =
        signedUrlsQuery.data.mp3_download_url ?? signedUrlsQuery.data.original_download_url;
      setAudioUrl(url ?? null);
    }
  }, [signedUrlsQuery.data]);

  const handleDownload = async (type: "original" | "mp3") => {
    setIsLoadingUrl(true);
    try {
      const response = await api.get(
        "/internal/v1/captured-audio/{captured_audio_id}/signed-urls",
        {
          params: { path: { captured_audio_id: capturedAudioId } },
        }
      );
      const url =
        type === "original"
          ? response.data?.original_download_url
          : response.data?.mp3_download_url;
      if (url) {
        window.open(url, "_blank");
      } else {
        alert(`${type === "original" ? "Original" : "MP3"} file is not available.`);
      }
    } catch (err) {
      alert(`Failed to download: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const canPlay = hasFile || hasMp3;

  return (
    <Section>
      <SectionTitle>Audio Playback</SectionTitle>
      <PlayerContainer>
        {canPlay && audioUrl ? (
          <AudioElement ref={audioRef} controls src={audioUrl} preload="metadata" />
        ) : canPlay && signedUrlsQuery.isLoading ? (
          <NoAudioMessage>Loading audio...</NoAudioMessage>
        ) : (
          <NoAudioMessage>No audio file available for playback</NoAudioMessage>
        )}

        <MetadataGrid>
          <MetadataItem>
            <MetadataLabel>Duration</MetadataLabel>
            <MetadataValue>
              {durationSeconds != null ? formatDuration(durationSeconds) : "Unknown"}
            </MetadataValue>
          </MetadataItem>
          <MetadataItem>
            <MetadataLabel>Format</MetadataLabel>
            <MetadataValue>{mimeType || "Unknown"}</MetadataValue>
          </MetadataItem>
          <MetadataItem>
            <MetadataLabel>Original File</MetadataLabel>
            <FileStatus available={hasFile}>{hasFile ? "Uploaded" : "Not uploaded"}</FileStatus>
          </MetadataItem>
          <MetadataItem>
            <MetadataLabel>MP3 Conversion</MetadataLabel>
            <FileStatus available={hasMp3}>{hasMp3 ? "Available" : "Not available"}</FileStatus>
          </MetadataItem>
        </MetadataGrid>

        <ButtonRow>
          <Button
            type="button"
            variant="soft"
            onClick={() => handleDownload("original")}
            disabled={!hasFile || isLoadingUrl}
          >
            {isLoadingUrl ? "Loading..." : "Download Original"}
          </Button>
          <Button
            type="button"
            variant="soft"
            onClick={() => handleDownload("mp3")}
            disabled={!hasMp3 || isLoadingUrl}
          >
            {isLoadingUrl ? "Loading..." : "Download MP3"}
          </Button>
        </ButtonRow>
      </PlayerContainer>
    </Section>
  );
};
